import { supabase } from "@/lib/supabaseClient";
import type {
  EmployeeIssuanceReadinessRow,
  IssuePayrollRunBankingMode,
  IssuePayrollRunResponse,
  IssuerQueueRun,
  PayrollRunReturnItem,
} from "./mappers";
import {
  asErrorMessage,
} from "./mappers";

type IssuerQueueBundleRow = {
  submitted_runs: unknown;
  returned_runs: unknown;
  issued_runs_history: unknown;
};

export type IssuerQueueBundle = {
  submittedRuns: IssuerQueueRun[];
  returnedRuns: IssuerQueueRun[];
  issuedRunsHistory: IssuerQueueRun[];
};

type IssuerReviewDetailBundleRow = {
  header: unknown;
  workflow_timeline: unknown;
  issued_data: unknown;
  operational_data: unknown;
  integrity_data: unknown;
};

export type IssuerReviewDetailBundle = {
  header: unknown;
  workflowTimeline: unknown;
  issuedData: unknown;
  operationalData: unknown;
  integrityData: unknown;
};

type IssuedPayrollUiBundleRow = {
  header: unknown;
  employee_lines: unknown;
  artifacts: unknown;
  integrity_status: unknown;
};

export type IssuedPayrollUiBundle = {
  header: unknown;
  employeeLines: unknown;
  artifacts: unknown;
  integrityStatus: unknown;
};

function getSingleRow<T>(data: unknown): T | null {
  if (Array.isArray(data)) {
    return (data[0] ?? null) as T | null;
  }

  if (data && typeof data === "object") {
    return data as T;
  }

  return null;
}

type IssuerQueueRunRpcRow = Omit<IssuerQueueRun, "id" | "state"> & {
  id?: string | null;
  run_id?: string | null;
  state?: IssuerQueueRun["state"] | null;
};

function asIssuerQueueRuns(value: unknown): IssuerQueueRun[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .map((row): IssuerQueueRun | null => {
      if (!row || typeof row !== "object") {
        return null;
      }

      const candidate = row as IssuerQueueRunRpcRow;
      const id = candidate.id ?? candidate.run_id ?? null;

      if (!id) {
        return null;
      }

      return {
        ...candidate,
        id,
        state: candidate.state ?? candidate.status,
      } as IssuerQueueRun;
    })
    .filter((row): row is IssuerQueueRun => row !== null);
}

function asEmployeeIssuanceReadinessRows(
  value: unknown
): EmployeeIssuanceReadinessRow[] {
  return Array.isArray(value)
    ? (value as EmployeeIssuanceReadinessRow[])
    : [];
}

/**
 * ISSUER QUEUE
 */
export async function getIssuerQueueBundle(
  companyId: string
): Promise<IssuerQueueBundle> {
  const { data, error } = await supabase
    .schema("payroll")
    .rpc("get_issuer_queue_bundle", {
      p_company_id: companyId,
    });

  if (error) {
    throw new Error(asErrorMessage(error));
  }

  const row = getSingleRow<IssuerQueueBundleRow>(data);

  return {
    submittedRuns: asIssuerQueueRuns(row?.submitted_runs),
    returnedRuns: asIssuerQueueRuns(row?.returned_runs),
    issuedRunsHistory: asIssuerQueueRuns(row?.issued_runs_history),
  };
}

export async function getIssuerQueueRuns(
  companyId: string
): Promise<IssuerQueueRun[]> {
  const { data, error } = await supabase
    .schema("payroll")
    .rpc("get_issuer_queue_runs", {
      p_company_id: companyId,
    });

  if (error) {
    throw new Error(asErrorMessage(error));
  }

  return asIssuerQueueRuns(data);
}

/**
 * ISSUER REVIEW DETAIL
 */
export async function getIssuerReviewDetailBundle(
  runId: string
): Promise<IssuerReviewDetailBundle> {
  const { data, error } = await supabase
    .rpc("get_issuer_review_detail_bundle", {
      p_run_id: runId,
    });

  if (error) {
    throw new Error(asErrorMessage(error));
  }

  const row = getSingleRow<IssuerReviewDetailBundleRow>(data);

  return {
    header: row?.header ?? null,
    workflowTimeline: row?.workflow_timeline ?? null,
    issuedData: row?.issued_data ?? null,
    operationalData: row?.operational_data ?? null,
    integrityData: row?.integrity_data ?? null,
  };
}

/**
 * ISSUED PAYROLL UI
 */
export async function getIssuedPayrollUiBundle(
  runId: string
): Promise<IssuedPayrollUiBundle> {
  const { data, error } = await supabase
    .schema("payroll")
    .rpc("get_issued_payroll_ui_bundle", {
      p_run_id: runId,
    });

  if (error) {
    throw new Error(asErrorMessage(error));
  }

  const row = getSingleRow<IssuedPayrollUiBundleRow>(data);

  return {
    header: row?.header ?? null,
    employeeLines: row?.employee_lines ?? null,
    artifacts: row?.artifacts ?? null,
    integrityStatus: row?.integrity_status ?? null,
  };
}

export async function getIssuedPayrollOperationalBundle(
  runId: string
): Promise<unknown> {
  const { data, error } = await supabase
    .schema("payroll")
    .rpc("get_issued_payroll_operational_bundle", {
      p_run_id: runId,
    });

  if (error) {
    throw new Error(asErrorMessage(error));
  }

  return getSingleRow<unknown>(data);
}

export async function getIssuedPayrollReplayIntegrityBundle(
  runId: string
): Promise<unknown> {
  const { data, error } = await supabase
    .schema("payroll")
    .rpc("get_issued_payroll_replay_integrity_bundle", {
      p_run_id: runId,
    });

  if (error) {
    throw new Error(asErrorMessage(error));
  }

  return getSingleRow<unknown>(data);
}

export async function getIssuedRunsHistory(): Promise<IssuerQueueRun[]> {
  const { data, error } = await supabase
    .schema("payroll")
    .rpc("get_issued_runs_history");

  if (error) {
    throw new Error(asErrorMessage(error));
  }

  return asIssuerQueueRuns(data);
}

export async function listPayrollRunReturnItems(
  runId: string
): Promise<PayrollRunReturnItem[]> {
  const { data, error } = await supabase
    .schema("payroll")
    .rpc("list_payroll_run_return_items", {
      p_run_id: runId,
    });

  if (error) {
    throw new Error(asErrorMessage(error));
  }

  return Array.isArray(data)
    ? (data as PayrollRunReturnItem[])
    : [];
}

export async function getEmployeeIssuanceReadiness(params: {
  companyId: string;
  employeeId?: string | null;
}): Promise<EmployeeIssuanceReadinessRow[]> {
  const { data, error } = await supabase
    .rpc("get_employee_issuance_readiness", {
      p_company_id: params.companyId,
      p_employee_id: params.employeeId ?? null,
    });

  if (error) {
    throw new Error(asErrorMessage(error));
  }

  return asEmployeeIssuanceReadinessRows(data);
}

export async function getRunIssuanceReadiness(
  runId: string
): Promise<EmployeeIssuanceReadinessRow[]> {
  const { data, error } = await supabase
    .schema("payroll")
    .rpc("get_run_issuance_readiness", {
      p_run_id: runId,
    });

  if (error) {
    throw new Error(asErrorMessage(error));
  }

  return asEmployeeIssuanceReadinessRows(data);
}

export async function addPayrollRunReturnItem(params: {
  runId: string;
  reason: string;
  severity: string;
  employeeProfileId?: string | null;
  fieldKey?: string | null;
  correctionNote: string;
}): Promise<string> {
  const { data, error } = await supabase
    .schema("payroll")
    .rpc("add_payroll_run_return_item", {
      p_run_id: params.runId,
      p_reason: params.reason,
      p_severity: params.severity,
      p_employee_profile_id:
        params.employeeProfileId ?? null,
      p_field_key:
        params.fieldKey ?? null,
      p_correction_note:
        params.correctionNote,
    });

  if (error) {
    throw new Error(asErrorMessage(error));
  }

  return String(data);
}

export async function escalateReturnItemToResolver(params: {
  returnItemId: string;
  resolverUserId: string;
  reason?: string;
}): Promise<string> {
  const { data, error } = await supabase
    .schema("payroll")
    .rpc("escalate_return_item_to_resolver", {
      p_return_item_id: params.returnItemId,
      p_resolver_user_id: params.resolverUserId,
      p_reason: params.reason ?? "",
    });

  if (error) {
    throw new Error(asErrorMessage(error));
  }

  return String(data);
}

export async function acceptResolutionAssignment(
  assignmentId: string
): Promise<void> {
  const { error } = await supabase
    .schema("payroll")
    .rpc("accept_resolution_assignment", {
      p_assignment_id: assignmentId,
    });

  if (error) {
    throw new Error(asErrorMessage(error));
  }
}

export async function cancelResolutionAssignment(
  assignmentId: string,
  reason: string
): Promise<void> {
  const { error } = await supabase
    .schema("payroll")
    .rpc("cancel_resolution_assignment", {
      p_assignment_id: assignmentId,
      p_reason: reason,
    });

  if (error) {
    throw new Error(asErrorMessage(error));
  }
}

export async function resolvePayrollRunReturnItem(params: {
  returnItemId: string;
  overrideReason?: string | null;
  overrideSessionActorId?: string | null;
}): Promise<void> {
  const { error } = await supabase
    .schema("payroll")
    .rpc("resolve_payroll_run_return_item", {
      p_return_item_id: params.returnItemId,
      p_override_reason: params.overrideReason ?? null,
      p_override_session_actor_id: params.overrideSessionActorId ?? null,
    });

  if (error) {
    throw new Error(asErrorMessage(error));
  }
}

export async function getAuthorizedResolverQueue(
  companyId: string
): Promise<PayrollRunReturnItem[]> {
  const { data, error } = await supabase
    .schema("payroll")
    .rpc("get_authorized_resolver_queue", {
      p_company_id: companyId,
    });

  if (error) {
    throw new Error(asErrorMessage(error));
  }

  return Array.isArray(data)
    ? (data as PayrollRunReturnItem[])
    : [];
}

/**
 * ISSUER ACTIONS
 */

export async function returnRunToClerk(
  runId: string,
  reason: string,
  note: string
): Promise<void> {
  const { error } = await supabase
    .schema("payroll")
    .rpc("return_run_to_clerk", {
      p_run_id: runId,
      p_reason: reason,
      p_note: note,
    });

  if (error) {
    throw new Error(asErrorMessage(error));
  }
}

export async function issuePayrollRun(params: {
  runId: string;
  bankingMode: IssuePayrollRunBankingMode;
}): Promise<IssuePayrollRunResponse> {
  const { error } = await supabase
    .schema("payroll")
    .rpc("issue_run", {
      p_run_id: params.runId,
    });

  if (error) {
    throw new Error(asErrorMessage(error));
  }

  return {
    bank_transmission_id: "",
    journal_entry_id: "",
  };
}